/**
 * StatusBadge — badge premium unifié Coach DIA.
 *
 * - Pill h-6, px-2.5, rounded-full, text-xs semibold
 * - Dot coloré 6px à gauche + pictogramme Lucide optionnel
 * - Palette sophistiquée (pas de Tailwind "default")
 * - Casse naturelle (pas d'uppercase)
 * - Fusion automatique de l'état « Reprogrammée »
 */
import React from 'react';
import {
  CheckCircle2, Clock, AlertTriangle, XCircle, Sparkles,
  FileText, RefreshCw, Hourglass, Wallet, Ban,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/* ─── Tooltip explanations (premium UX — explicit consequences) ─── */
const DESCRIPTIONS = {
  confirmed: 'Séance confirmée. Annulation gratuite jusqu\'à 24 h avant le créneau.',
  confirmed_rescheduled: 'Séance confirmée et déjà reportée une fois (limite atteinte).',
  reschedule_pending_coach: 'Votre coach propose un nouveau créneau. Répondez depuis votre espace.',
  reschedule_pending_user: 'Votre demande de report est en attente.',
  completed: 'Séance terminée. Un rapport IA peut être disponible.',
  report_generated: 'Le rapport IA post-séance est disponible.',
  validated: 'Séance validée par le coach.',
  to_pay: 'Séance en attente de règlement au coach.',
  coach_paid: 'Coach rémunéré. Séance clôturée.',
  pending_payment: 'Le paiement n\'est pas encore confirmé. Le créneau est libéré après 30 min sans paiement.',
  expired: 'Créneau libéré automatiquement faute de paiement dans le délai.',
  cancelled_refunded: 'Annulée à plus de 24 h. Remboursement intégral en cours (5–10 jours bancaires).',
  cancelled_not_eligible: 'Annulation à moins de 24 h — aucun remboursement, conformément aux conditions.',
  cancelled_refund_failed: 'Remboursement en cours de traitement. Notre équipe revient vers vous sous 24 h.',
  cancelled: 'Séance annulée.',
};

/* ─── Palette premium (bg / border / text / dot) ─── */
const TONES = {
  emerald: {
    bg: '#ECFDF5', border: '#A7F3D0', text: '#047857', dot: '#059669',
  },
  violet: {
    bg: '#F5F3FF', border: '#DDD6FE', text: '#5B21B6', dot: '#7C3AED',
  },
  gold: {
    bg: '#FEF7E0', border: '#F3D060', text: '#92710A', dot: '#B8860B',
  },
  amber: {
    bg: '#FFFBEB', border: '#FCD34D', text: '#92400E', dot: '#D97706',
  },
  red: {
    bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', dot: '#DC2626',
  },
  blue: {
    bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF', dot: '#2563EB',
  },
  slate: {
    bg: '#F8FAFC', border: '#E2E8F0', text: '#64748B', dot: '#94A3B8',
  },
};

/* ─── Libellés + tons + icônes (source de vérité UI) ─── */
export function describeBookingStatus(b) {
  if (!b) return { label: 'Inconnue', tone: 'slate', icon: null, descKey: null };
  const { status, refund_status, reschedule_request, reschedule_count } = b;

  // Demande de report en attente — prioritaire
  if (status === 'confirmed' && reschedule_request?.status === 'pending') {
    const fromCoach = reschedule_request.proposed_by === 'coach';
    return {
      label: fromCoach ? 'Report demandé par le coach' : 'Report demandé',
      tone: 'violet',
      icon: Sparkles,
      descKey: fromCoach ? 'reschedule_pending_coach' : 'reschedule_pending_user',
    };
  }

  // Fusion "Confirmée · Reprogrammée"
  if (status === 'confirmed' && reschedule_count > 0) {
    return {
      label: 'Confirmée · Reprogrammée',
      tone: 'emerald',
      icon: CheckCircle2,
      descKey: 'confirmed_rescheduled',
    };
  }

  switch (status) {
    case 'confirmed':
      return { label: 'Confirmée', tone: 'emerald', icon: CheckCircle2, descKey: 'confirmed' };
    case 'completed':
      return { label: 'Terminée', tone: 'blue', icon: CheckCircle2, descKey: 'completed' };
    case 'report_generated':
      return { label: 'Rapport disponible', tone: 'gold', icon: FileText, descKey: 'report_generated' };
    case 'validated':
      return { label: 'Validée', tone: 'blue', icon: CheckCircle2, descKey: 'validated' };
    case 'to_pay':
      return { label: 'À payer', tone: 'gold', icon: Wallet, descKey: 'to_pay' };
    case 'coach_paid':
      return { label: 'Coach payé', tone: 'emerald', icon: CheckCircle2, descKey: 'coach_paid' };
    case 'pending_payment':
      return { label: 'Paiement en attente', tone: 'amber', icon: Hourglass, descKey: 'pending_payment' };
    case 'expired':
      return { label: 'Expirée', tone: 'slate', icon: Ban, descKey: 'expired' };
    case 'cancelled':
      if (refund_status === 'refunded') {
        return { label: 'Annulée · remboursée', tone: 'emerald', icon: RefreshCw, descKey: 'cancelled_refunded' };
      }
      if (refund_status === 'not_eligible') {
        return { label: 'Annulée · non remboursée', tone: 'red', icon: XCircle, descKey: 'cancelled_not_eligible' };
      }
      if (refund_status === 'failed') {
        return { label: 'Remboursement en cours', tone: 'amber', icon: Clock, descKey: 'cancelled_refund_failed' };
      }
      return { label: 'Annulée', tone: 'red', icon: XCircle, descKey: 'cancelled' };
    default:
      return { label: status || 'Inconnue', tone: 'slate', icon: null, descKey: null };
  }
}

/* ─── Composant visuel ─── */
export default function StatusBadge({ booking, withIcon = true, className = '', withTooltip = true }) {
  const { label, tone, icon: Icon, descKey } = describeBookingStatus(booking);
  const palette = TONES[tone] || TONES.slate;
  const description = descKey ? DESCRIPTIONS[descKey] : null;

  const pill = (
    <span
      data-testid={`status-badge-${booking?.status || 'unknown'}`}
      className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold border whitespace-nowrap
                  transition-all duration-150 cursor-default select-none
                  hover:shadow-[0_2px_8px_rgba(28,17,71,0.08)] hover:-translate-y-[0.5px] ${className}`}
      style={{
        background: palette.bg,
        borderColor: palette.border,
        color: palette.text,
      }}
    >
      <span
        aria-hidden="true"
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ background: palette.dot }}
      />
      {withIcon && Icon && <Icon className="w-3 h-3 flex-shrink-0" strokeWidth={2.4} />}
      <span className="leading-none">{label}</span>
    </span>
  );

  if (!withTooltip || !description) return pill;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[260px] bg-[#1C1147] text-white border-none text-xs leading-relaxed px-3 py-2 shadow-xl"
        >
          {description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
